import React, { useState } from 'react';
import { Grid, TextField, Typography, Box, Button, Card, CardContent, Select, MenuItem, FormControl, InputLabel, useTheme } from '@mui/material';

import { generateTenantToken, makeEncryptedApiCall } from "./utils/apiUtils";

function BankCreditCardForm({ setScore, onComputed, setErrorMessage, setOpenSnackbar, setIsLoading, setFixedPay, setVariablePay }) {
  const theme = useTheme();
  const [tenant, setTenant] = useState({ tenantId: '1', tenantName: '' });
  const [form, setForm] = useState({ 
    income: '5,00,000', 
    cardType: 'Standard',
    cibil: '720',
    gender: "Male",
    state: "Maharashtra",
    age: '27',
    occupation: "Salaried",
    education_level: "Graduate",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTenant = (e) => setTenant({ ...tenant, [e.target.name]: e.target.value });
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const params = [
    { name: 'Income Profile', score: 80, weight: 0.6 },
    { name: 'Card Tier', score: 70, weight: 0.4 },
    { name: 'Credit Reliability', score: 70, weight: 0.8 },
    { name: 'Customer Spending', score: 70, weight: 0.3 },
    { name: 'Payment History Score', score: 70, weight: 0.7 }
  ];
  const formula = '(80 * 0.6) + (70 * 0.4) = 76';

  const handleSubmit = async () => {
    if (!tenant.tenantId.trim() || !tenant.tenantName.trim()) {
      setErrorMessage("Please enter both Tenant ID and Tenant Name.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    try {
      // Step 1: Generate tenant token
      const tokenResponse = await generateTenantToken(tenant.tenantId, tenant.tenantName);
      
      // Step 2: Make encrypted API call
      const url = `/api/v1.0/tenants/${tenant.tenantId}/score/bank/creditCard`;
      const score = await makeEncryptedApiCall(url, form, tokenResponse.token);
      
      setScore(score.output);
      const weights = params.reduce((obj, p) => { obj[p.name.replace(/\s+/g, '_').toLowerCase()] = p.weight; return obj; }, {});
    // setScore(scores);
      onComputed && onComputed({ weights, finalScore: 76, finalPay: 450, formula });
      // setIsSubmitting(false);
      setFixedPay(1000);
      setVariablePay(300);
    } catch (error) {
      console.error("Error fetching bank score:", error);
      let detailedMessage = "An error occurred while fetching the bank score.";
      if (error.response && error.response.data && error.response.data.error) {
        detailedMessage = error.response.data.error;
      } else if (error.message) {
        detailedMessage = error.message;
      }
      setErrorMessage(detailedMessage);
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Tenant Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField name="tenantId" value={tenant.tenantId} onChange={handleTenant} fullWidth size="small" placeholder="Tenant ID" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField name="tenantName" value={tenant.tenantName} onChange={handleTenant} fullWidth size="small" placeholder="Tenant Name" autoComplete="off" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {[
        { name: 'income', label: 'Customer Income' },
        { name: 'age', label: 'Customer Age' },
        { name: 'gender', label: 'Customer Gender' },
        { name: 'state', label: 'Customer State' },
        { name: 'cibil', label: 'Customer CIBIL Score' },
        { name: 'occupation', label: 'Customer Occupation' },
        { name: 'education_level', label: 'Customer Education Level' }
      ].map((f) => (
        <Grid item xs={12} sm={6} key={f.name}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={500}>{f.label}</Typography>
          </Box>
          <TextField name={f.name} value={form[f.name]} onChange={handleChange} fullWidth size="small" placeholder={`Enter ${f.label}`} />
        </Grid>
      ))}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel id="cardtype-label">Card Type Requested</InputLabel>
          <Select labelId="cardtype-label" label="Card Type Requested" name="cardType" value={form.cardType} onChange={handleChange}>
            <MenuItem value="Platinum">Platinum</MenuItem>
            <MenuItem value="Gold">Gold</MenuItem>
            <MenuItem value="Standard">Standard</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
      <Button 
        variant="contained" 
        fullWidth 
        size="large"
        onClick={handleSubmit} 
        disabled={isSubmitting} 
        sx={{
          mt: 2, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1rem', fontWeight: 600,
          background: 'linear-gradient(135deg,rgb(88, 139, 249) 0%, #2563EB 100%)',
          boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
          '&:hover': { boxShadow: '0 15px 30px rgba(37, 99, 235, 0.3)', transform: 'translateY(-2px)' },
          transition: 'all 0.3s ease',
          '&.Mui-disabled': {
              background: theme.palette.grey[300],
              boxShadow: 'none',
              color: theme.palette.grey[500],
              cursor: 'not-allowed'
          }
        }}

        >
          {isSubmitting ? 'Calculating...' : 'Calculate Credit Cards Score'}
        </Button>
      </Grid>
    </Grid>
  );
}

export default BankCreditCardForm;


